import { useCallback } from 'react';
import type { HistoryItem } from '../components/Timeline/HistoryTimeline';

interface PrintHistoryParams {
  items: HistoryItem[];
  demandaTitulo: string;
  demandaId: number;
  status: string;
  prioridade: string;
}

export const usePrintHistory = () => {
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionIcon = (type: HistoryItem['type']) => {
    switch (type) {
      case 'created':
        return '✨';
      case 'assigned':
        return '👤';
      case 'status_changed':
        return '✓';
      case 'priority_changed':
        return '⚠️';
      case 'commented':
        return '💬';
      case 'updated':
        return '✏️';
      case 'watched':
        return '👁️';
      case 'attachment_added':
        return '📎';
      case 'attachment_removed':
        return '🗑️';
      default:
        return '•';
    }
  };

  const getActionText = (item: HistoryItem) => {
    const descLower = item.description.toLowerCase();

    // Ações específicas baseadas na descrição exata do backend
    // 1. Autorizou a demanda
    if (descLower.includes('autorizou a demanda')) return 'autorizou a demanda';
    
    // 2. Reprovou a demanda
    if (descLower.includes('reprovou a demanda')) return 'reprovou a demanda';
    
    // 3. Editou a demanda
    if (descLower.includes('editou a demanda')) return 'editou a demanda';
    
    // 4. Atribuiu a demanda para si
    if (descLower.includes('atribuiu a demanda para si')) return 'atribuiu a demanda para si';
    
    // 5. Solucionou sua parte da demanda
    if (descLower.includes('solucionou sua parte da demanda')) return 'solucionou sua parte da demanda';
    
    // 6. Solucionou a demanda
    if (descLower.includes('solucionou a demanda')) return 'solucionou a demanda';
    
    // 7. Finalizou a demanda
    if (descLower.includes('finalizou a demanda')) return 'finalizou a demanda';
    
    // 8. Devolveu a demanda
    if (descLower.includes('devolveu a demanda')) return 'devolveu a demanda';
    
    // 9. Demanda enviada errada
    if (descLower.includes('demanda enviada errada')) return 'marcou a demanda como enviada errada';
    
    // 10. Reenviou a demanda
    if (descLower.includes('reenviou a demanda')) {
      // Se é expansível, mostrar contagem de alterações
      if (item.expandable && item.changes && item.changes.length > 0) {
        const alteracoes = item.changes.length;
        return `reenviou a demanda (${alteracoes} alteraç${alteracoes === 1 ? 'ão' : 'ões'})`;
      }
      return 'reenviou a demanda';
    }
    
    // 11. Desfez a solução da demanda
    if (descLower.includes('desfez a solução')) return 'desfez a solução da demanda';
    
    // 12. Recusou a demanda
    if (descLower.includes('recusou a demanda')) return 'recusou a demanda';
    
    // 13. Aceitou a demanda
    if (descLower.includes('aceitou a demanda')) return 'aceitou a demanda';
    
    // 14. Atribuiu a demanda para usuários
    if (descLower.includes('atribuiu a demanda para usuários') || descLower.includes('atribuiu a demanda para usuário')) {
      return 'atribuiu a demanda para usuários';
    }
    
    // 15. Removeu um atribuido da demanda
    if (descLower.includes('removeu um atribuido') || descLower.includes('removeu um atribuído')) {
      return 'removeu um atribuído da demanda';
    }
    
    // 16. Fez um comentário
    if (descLower.includes('fez um comentário')) return 'fez um comentário';
    
    // 17 e 18. Adicionou anexos (anexar e anexarComComentarios)
    if (descLower.includes('adicionou anexos')) return 'adicionou anexos';
    
    // 19. Removeu anexos
    if (descLower.includes('removeu anexos')) return 'removeu anexos';
    
    // 20. Adicionou observadores
    if (
      (descLower.includes('observador') || descLower.includes('observadores')) &&
      (descLower.includes('adicion') || descLower.includes('ativ'))
    ) {
      const alvo = item?.details?.affected_name;
      return alvo ? `adicionou ${alvo} como observador` : 'adicionou observadores';
    }
    
    // 21. Removeu observador
    if (
      (descLower.includes('observador') || descLower.includes('observadores')) &&
      (descLower.includes('remov') || descLower.includes('exclu') || descLower.includes('desativ'))
    ) {
      const alvo = item?.details?.affected_name;
      return alvo ? `removeu ${alvo} como observador` : 'removeu observador';
    }
    
    // 22. Recusou ser um observador
    if (descLower.includes('recusou ser um observador') || descLower.includes('recusou ser observador')) {
      return 'recusou ser um observador';
    }
    
    // 23. Excluiu a demanda
    if (descLower.includes('excluiu a demanda')) return 'excluiu a demanda';

    // Fallback genérico para criação
    if (descLower.includes('criou')) return 'criou esta demanda';

    // Mudanças de status genéricas
    if (item.type === 'status_changed' && item.details?.from && item.details?.to) {
      return `mudou o status de "${item.details.from}" para "${item.details.to}"`;
    }

    // Mudanças de prioridade
    if (item.type === 'priority_changed' && item.details?.from && item.details?.to) {
      return `alterou a prioridade de "${item.details.from}" para "${item.details.to}"`;
    }

    // Anexos individuais
    if (item.type === 'attachment_added' && item.details?.attachment_name) {
      return `anexou o arquivo "${item.details.attachment_name}"`;
    }
    if (item.type === 'attachment_removed' && item.details?.attachment_name) {
      return `removeu o arquivo "${item.details.attachment_name}"`;
    }

    // Edições expansíveis
    if (item.expandable && item.changes) {
      return `editou a demanda (${item.changes.length} alteraç${item.changes.length === 1 ? 'ão' : 'ões'})`;
    }

    // Fallback - usar descrição original
    return item.description;
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatPagamentos = (pagamentos: any[]): string => {
    if (!Array.isArray(pagamentos) || pagamentos.length === 0) return 'Nenhum pagamento';

    return pagamentos
      .map((pag, idx) => {
        const nota = pag?.nota || 'Sem nota';
        const data = pag?.data_pagamento
          ? new Date(pag.data_pagamento).toLocaleDateString('pt-BR')
          : 'Sem data';
        return `Pagamento ${idx + 1}: ${nota} (${data})`;
      })
      .join(' | ');
  };

  const formatTemplateValue = (value: any): string => {
    if (!value) return 'Dados não disponíveis';

    let parsed: any = value;

    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        if (value === '[object Object]') {
          return 'Dados do formulário atualizados';
        }
        return stripHtml(value);
      }
    }

    const respostas = parsed?.respostas || parsed;
    if (!respostas || typeof respostas !== 'object') {
      return 'Dados não disponíveis';
    }

    const formattedParts: string[] = [];

    Object.entries(respostas).forEach(([key, val]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1);

      if (key === 'pagamentos' && Array.isArray(val)) {
        formattedParts.push(`${label}: ${formatPagamentos(val)}`);
      } else if (Array.isArray(val)) {
        formattedParts.push(`${label}: ${val.length ? `${val.length} item(ns)` : 'Nenhum item'}`);
      } else if (typeof val === 'string' && val.trim()) {
        const clean = stripHtml(val).trim();
        if (clean) {
          formattedParts.push(`${label}: ${clean}`);
        }
      } else if (val !== null && val !== undefined && val !== '') {
        formattedParts.push(`${label}: ${String(val)}`);
      }
    });

    return formattedParts.length > 0 ? formattedParts.join(' | ') : 'Sem dados';
  };

  /**
   * Formata valor de mudança para exibição legível
   * Trata especialmente objetos como template_respostas
   */
  const formatChangeValue = (
    value: string | Record<string, unknown> | unknown,
    fieldName?: string
  ): string => {
    if (value === null || value === undefined || value === '') return '(vazio)';

    if (fieldName === 'template_respostas' || fieldName === 'Dados do Formulário') {
      return formatTemplateValue(value);
    }

    if (typeof value === 'string') {
      if (value === '[object Object]') {
        return 'Dados atualizados';
      }
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null) {
          return JSON.stringify(parsed, null, 2);
        }
      } catch {
        // Não é JSON válido, seguir com string limpa
      }
      const stripped = stripHtml(value);
      return stripped || '(vazio)';
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return 'Nenhum item';
        }
        return value
          .map(item => (typeof item === 'string' ? stripHtml(item) : String(item)))
          .join(', ');
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '(dados complexos)';
      }
    }

    return String(value);
  };

  const parseAttachmentData = (comment: string) => {
    try {
      const data = JSON.parse(comment);
      if (data.anexos && Array.isArray(data.anexos)) {
        return {
          attachments: data.anexos.map((anexo: any) => ({
            id: anexo.id,
            name: anexo.nome_anexo,
            comment: anexo.comentario_individual || '', // ✅ Captura comentário individual
          })),
          comment: data.comentario_geral || data.comentario || '', // ✅ Comentário geral
          hasIndividualComments: data.tem_comentarios_individuais || false,
        };
      }
    } catch {
      // Se não conseguir fazer parse, retorna null
    }
    return null;
  };

  const generateHistoryHTML = ({ items, demandaTitulo, demandaId, status, prioridade }: PrintHistoryParams) => {
    const itemsHTML = items.map((item, index) => {
      let commentHTML = '';
      let attachmentsHTML = '';
      let changesHTML = '';

      // Processar comentários
      if (item.details?.comment) {
        const attachmentData = parseAttachmentData(item.details.comment);
        let commentText = '';

        if (item.type === 'attachment_added' || item.type === 'attachment_removed' || item.type === 'created') {
          if (attachmentData) {
            // Comentário geral (separado dos anexos)
            commentText = attachmentData.comment ? stripHtml(attachmentData.comment) : '';
            
            // Processar anexos
            if (attachmentData.attachments.length > 0) {
              const bgColor = item.type === 'attachment_removed' ? '#fef2f2' : '#faf5ff';
              const borderColor = item.type === 'attachment_removed' ? '#f43f5e' : '#a855f7';
              const iconColor = item.type === 'attachment_removed' ? '#f43f5e' : '#a855f7';
              const icon = item.type === 'attachment_removed' ? '🗑️' : '📎';
              const label = item.type === 'attachment_removed' ? 'Anexos removidos:' : 'Anexos adicionados:';

              attachmentsHTML = `
                <div style="margin-top: 10px; margin-left: 30px; padding: 12px; background-color: ${bgColor}; border-left: 3px solid ${borderColor}; border-radius: 4px;">
                  <div style="font-size: 12px; font-weight: bold; color: ${iconColor}; margin-bottom: 6px;">
                    ${icon} ${label}
                  </div>
                  <ul style="margin: 0; padding-left: 0; list-style: none; font-size: 13px; color: #374151;">
                    ${attachmentData.attachments.map((att: { id: number; name: string; comment: string }) => `
                      <li style="margin-bottom: 10px; padding: 8px; background-color: white; border-radius: 4px; border: 1px solid #e5e7eb;">
                        <div style="font-weight: 500; color: #111827; margin-bottom: 4px;">
                          📄 ${att.name}
                        </div>
                        ${att.comment && att.comment.trim() ? `
                          <div style="margin-top: 6px; padding: 6px; background-color: #f3f4f6; border-left: 2px solid #6366f1; border-radius: 3px;">
                            <div style="font-size: 11px; font-weight: bold; color: #6366f1; margin-bottom: 3px;">
                              💬 Comentário do arquivo:
                            </div>
                            <div style="font-size: 12px; color: #4b5563;">
                              ${att.comment}
                            </div>
                          </div>
                        ` : ''}
                      </li>
                    `).join('')}
                  </ul>
                </div>
              `;
            }
          } else {
            // Não é JSON de anexos, é comentário normal
            commentText = stripHtml(item.details.comment);
          }
        } else {
          commentText = stripHtml(item.details.comment);
        }

        // Renderizar comentário geral (se existir)
        if (commentText) {
          commentHTML = `
            <div style="margin-top: 10px; margin-left: 30px; padding: 12px; background-color: #f3f4f6; border-left: 3px solid #3b82f6; border-radius: 4px;">
              <div style="font-size: 12px; font-weight: bold; color: #3b82f6; margin-bottom: 6px;">
                💬 Comentário adicional:
              </div>
              <div style="font-size: 13px; color: #374151; line-height: 1.5;">
                ${commentText}
              </div>
            </div>
          `;
        }
      }

      // Processar mudanças detalhadas
      if (item.expandable && item.changes && item.changes.length > 0) {
        changesHTML = `
          <div style="margin-top: 10px; margin-left: 30px;">
            ${item.changes.map(change => `
              <div style="margin-bottom: 8px; padding: 10px; background-color: #fffbeb; border-left: 3px solid #f59e0b; border-radius: 4px;">
                <div style="font-size: 12px; font-weight: bold; color: #f59e0b; margin-bottom: 4px;">
                  ${change.fieldLabel}
                </div>
                <div style="font-size: 13px; color: #374151;">
                  <span style="color: #dc2626;">De: ${formatChangeValue(change.from, change.fieldLabel)}</span>
                  <span style="margin: 0 8px; color: #9ca3af;">→</span>
                  <span style="color: #059669;">Para: ${formatChangeValue(change.to, change.fieldLabel)}</span>
                </div>
              </div>
            `).join('')}
          </div>
        `;
      }

      return `
        <div style="margin-bottom: 25px; padding-bottom: 20px; border-bottom: ${index < items.length - 1 ? '1px solid #e5e7eb' : 'none'}; page-break-inside: avoid;">
          <div style="display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px;">
            <span style="font-size: 20px; line-height: 1;">${getActionIcon(item.type)}</span>
            <div style="flex: 1;">
              <div style="margin-bottom: 4px;">
                <strong style="font-size: 14px; color: #111;">${item.user.name}</strong>
                <span style="font-size: 14px; color: #666; margin-left: 8px;">
                  ${getActionText(item)}
                </span>
              </div>
              <div style="font-size: 12px; color: #999;">
                ${formatTimestamp(item.timestamp)} • ${item.user.funcao_usuario}
              </div>
            </div>
          </div>
          ${commentHTML}
          ${attachmentsHTML}
          ${changesHTML}
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Histórico da Demanda #${demandaId}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              background: white;
              padding: 20px;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 20px;
              }
              
              @page {
                margin: 2cm;
                size: A4;
              }
            }
            
            @media screen {
              body {
                background: #f5f5f5;
              }
              
              .print-container {
                background: white;
                box-shadow: 0 0 10px rgba(0,0,0,0.1);
                margin: 0 auto;
                max-width: 210mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container" style="padding: 20px;">
            <!-- Cabeçalho -->
            <div style="border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px;">
              <h1 style="margin: 0 0 10px 0; font-size: 24px; color: #333;">
                Histórico da Demanda #${demandaId}
              </h1>
              <h2 style="margin: 0 0 10px 0; font-size: 18px; color: #555; font-weight: normal;">
                ${demandaTitulo}
              </h2>
              <div style="display: flex; gap: 20px; font-size: 14px; color: #666; flex-wrap: wrap;">
                <div><strong>Status:</strong> ${status}</div>
                <div><strong>Prioridade:</strong> ${prioridade}</div>
                <div><strong>Data de Impressão:</strong> ${new Date().toLocaleString('pt-BR')}</div>
              </div>
            </div>

            <!-- Timeline do Histórico -->
            <div style="margin-top: 30px;">
              ${items.length === 0 ? `
                <p style="text-align: center; color: #999; padding: 40px 0;">
                  Nenhuma atividade registrada
                </p>
              ` : itemsHTML}
            </div>

            <!-- Rodapé -->
            <div style="margin-top: 40px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #999; text-align: center;">
              <p style="margin: 0;">
                Este documento foi gerado automaticamente pelo Sistema de Gestão de Demandas
              </p>
              <p style="margin: 5px 0 0 0;">
                Total de atividades: ${items.length} • Impresso em ${new Date().toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const printHistory = useCallback(({ items, demandaTitulo, demandaId, status, prioridade }: PrintHistoryParams) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor, permita pop-ups para imprimir o histórico');
      return;
    }

    const htmlContent = generateHistoryHTML({ items, demandaTitulo, demandaId, status, prioridade });
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Aguardar carregamento e imprimir
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        // Opcional: fechar a janela após impressão
        // printWindow.close();
      }, 250);
    };
  }, []);

  return { printHistory };
};
